import { Add, Delete } from '@mui/icons-material';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { LayoutItem } from 'api/models';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { fieldTemplatesState, layoutSelector } from 'state';
import LayoutFieldColumn from './LayoutFieldColumn';
import {
  getFieldIdsOfLayoutField,
  getNewColumnLayoutItem,
  getNewRowLayoutItem,
} from 'routes/FormTemplateEditor/FormEditor/utils';
import { produce } from 'immer';
import { useState } from 'react';
import { get, set } from 'object-path';
import OrganizationControls from './OrganizationControls';
import { getRemainingSpace } from 'routes/Form/utils';

interface LayoutFieldRowProps {
  row: LayoutItem;
  rowIndex: number;
  path: string;
  disabled: boolean;
}

export default function LayoutFieldRow({
  row,
  rowIndex,
  path,
  disabled,
}: LayoutFieldRowProps) {
  const [rowWidht, setRowWidth] = useState<number | undefined>(undefined);
  const { t } = useTranslation();
  const ref = (node: any) => {
    if (node !== null) {
      const cs = getComputedStyle(node);
      // got to calculate the exact width of the row so we can calculate the width of the columns
      const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const borderX =
        parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
      setRowWidth(node.offsetWidth - paddingX - borderX);
    }
  };

  const [layout, setLayout] = useRecoilState(layoutSelector);
  const [fieldTemplates, setFieldTemplates] =
    useRecoilState(fieldTemplatesState);

  const onAddColumn = (row: LayoutItem, rowIndex: number) => {
    const columns = get(layout, `${path}.children.${rowIndex}.children`);
    const remainingSpace = getRemainingSpace(columns);
    let newColumn = getNewColumnLayoutItem(remainingSpace);
    let items;
    if (remainingSpace === '0') {
      items = produce(layout, draft => {
        const columns = get(draft, `${path}.children.${rowIndex}.children`);
        columns.forEach((_column: LayoutItem, columnIndex: number) => {
          set(
            draft,
            `${path}.children.${rowIndex}.children.${columnIndex}.layoutOptions.layout`,
            `${Math.floor(12 / (columns.length + 1))}`,
          );
        });
        const remainingSpace = getRemainingSpace(columns);
        newColumn = getNewColumnLayoutItem(remainingSpace);
        get(draft, `${path}.children`)?.splice(rowIndex, 1, {
          ...row,
          children: [
            ...(get(draft, `${path}.children.${rowIndex}.children`) ?? []),
            newColumn,
          ],
        });
      });
    } else {
      items = produce(layout, draft => {
        get(draft, `${path}.children`)?.splice(rowIndex, 1, {
          ...row,
          children: [
            ...(get(draft, `${path}.children.${rowIndex}.children`) ?? []),
            newColumn,
          ],
        });
      });
    }
    setLayout(items);
  };

  const onAddRow = (rowIndex: number) => {
    setLayout(
      produce(layout, draft => {
        get(draft, `${path}.children`).splice(
          rowIndex + 1,
          0,
          getNewRowLayoutItem(),
        );
      }),
    );
  };

  const onDeleteRow = () => {
    const items = produce(layout, draft => {
      get(draft, `${path}.children`).splice(rowIndex, 1);
    });
    setLayout(items);

    const fieldItemsToBeRemoved = getFieldIdsOfLayoutField(
      get(layout, `${path}.children.${rowIndex}`),
    );

    const fieldItems = fieldTemplates.filter(fieldTemplate => {
      return !fieldItemsToBeRemoved?.includes(fieldTemplate.id);
    });

    setFieldTemplates(fieldItems);
  };

  return (
    <Box
      key={`layout-field-row-${row.id}`}
      sx={{
        backgroundColor: theme => theme.palette.action.hover,
        boxShadow: theme => `0px 0px 2px 1px ${theme.palette.divider}`,
        borderRadius: '5px',
        mb: 2,
        position: 'relative',
        pt: 1,
        pb: 1,
        maxWidth: '100%',
      }}
      ref={ref}
    >
      <Box
        display="flex"
        justifyContent={'end'}
        sx={{ mb: 2, maxWidth: '100%' }}
      >
        <OrganizationControls
          path={`${path}.children.${rowIndex}`}
          disabled={disabled}
        />
        <Button
          disabled={row?.children?.length === 12 || disabled}
          variant="text"
          size="small"
          onClick={() => onAddColumn(row, rowIndex)}
          sx={{
            border: theme => `2px dashed ${theme.palette.divider}`,
          }}
        >
          <Add /> {t('forms.addColumn')}
        </Button>
        <IconButton
          onClick={onDeleteRow}
          size="small"
          sx={{ ml: 1, mr: 1, width: '40px', height: '40px' }}
          disabled={disabled || disabled}
        >
          <Tooltip title={t('forms.deleteRow')}>
            <Delete fontSize="small" />
          </Tooltip>
        </IconButton>
      </Box>
      <Grid
        container
        wrap="nowrap"
        sx={{
          position: 'relative',
          justifyContent: row.layoutOptions?.style?.justifyContent ?? 'start',
          maxWidth: '100%',
        }}
      >
        {row?.children?.map((column, columnIndex) => (
          <LayoutFieldColumn
            key={`layout-field-column-${column.id}`}
            columnIndex={columnIndex}
            path={`${path}.children.${rowIndex}`}
            rowWidth={rowWidht ?? 0}
            editDisabled={disabled}
          />
        ))}
      </Grid>
      <Box display="flex" width="100%" justifyContent={'center'} sx={{ mt: 1 }}>
        <Button
          variant="text"
          size="small"
          onClick={() => onAddRow(rowIndex)}
          sx={{
            border: theme => `2px dashed ${theme.palette.divider}`,
          }}
          disabled={disabled}
        >
          <Add /> {t('forms.addRow')}
        </Button>
      </Box>
    </Box>
  );
}
